import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  OpportunitySchema,
  OpportunityCreateInputSchema,
  OpportunityUpdateInputSchema,
  type Opportunity,
  type OpportunityCreateInput,
  type OpportunityUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `opportunity` (piste commerciale, org-wide).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`OpportunityCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit
 * jamais un document pour découvrir ensuite qu'il est invalide.
 */

export type { OpportunityCreateInput };

/** Nom de la collection Firestore de la famille opportunity. */
export const OPPORTUNITIES_COLLECTION = 'opportunities';

/**
 * Repository typé pour la famille `opportunity`.
 * Toute lecture est validée par `OpportunitySchema`, toute écriture par
 * `OpportunityCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface OpportunityRepository {
  create(input: OpportunityCreateInput): Promise<Opportunity>;
  update(id: string, patch: OpportunityUpdateInput): Promise<Opportunity>;
  list(options?: ListOptions): Promise<Opportunity[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Opportunity | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Opportunity>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Opportunity>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Opportunity>;
}

const opportunityConverter = createConverter<Opportunity>((raw: DocumentData) => OpportunitySchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createOpportunityRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): OpportunityRepository {
  const col = collection(db, OPPORTUNITIES_COLLECTION).withConverter(opportunityConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Opportunity>(db, col, OPPORTUNITIES_COLLECTION, getActorId);

  return {
    async create(input: OpportunityCreateInput): Promise<Opportunity> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = OpportunityCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.contactId !== undefined) optional['contactId'] = fields.contactId;
      if (fields.stage !== undefined) optional['stage'] = fields.stage;
      if (fields.expectedValue !== undefined) optional['expectedValue'] = fields.expectedValue;
      if (fields.probability !== undefined) optional['probability'] = fields.probability;
      if (fields.source !== undefined) optional['source'] = fields.source;
      if (fields.expectedCloseAt !== undefined) optional['expectedCloseAt'] = fields.expectedCloseAt;
      if (fields.ownerWorkerId !== undefined) optional['ownerWorkerId'] = fields.ownerWorkerId;
      if (fields.lostReason !== undefined) optional['lostReason'] = fields.lostReason;
      if (fields.orderId !== undefined) optional['orderId'] = fields.orderId;
      if (fields.campaignId !== undefined) optional['campaignId'] = fields.campaignId;
      if (fields.messageId !== undefined) optional['messageId'] = fields.messageId;
      if (fields.agreementId !== undefined) optional['agreementId'] = fields.agreementId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Opportunity);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return OpportunitySchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: OpportunityUpdateInput): Promise<Opportunity> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = OpportunityUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, OPPORTUNITIES_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec OpportunitySchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`opportunity ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Opportunity[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Opportunity | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
